import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { HeartIcon } from '@heroicons/react/24/solid';
import { HeartIcon as HeartOutlineIcon } from '@heroicons/react/24/outline';
import { selectCurrentUser } from '../../redux/slices/authSlice';
import {
  useToggleLikeMutation,
  useGetLikeStatusQuery
} from '../../redux/apis';
import AuthRequiredModal from '../common/AuthRequiredModal';
import { handleUrlChange } from '../../redux/slices/filterSlice';

const Like = ({
  productId,
  size = 'md', // sm, md, lg, xl
  showCount = true,
  className = '',
  onUnauthorized = null
}) => {
  const currentUser = useSelector(selectCurrentUser);
  const dispatch = useDispatch();
  const [optimisticLiked, setOptimisticLiked] = useState(null);
  const [optimisticCount, setOptimisticCount] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  // Product API hooks
  const {
    data: likeStatus,
    isLoading: likeLoading
  } = useGetLikeStatusQuery(productId, {
    skip: !productId,
    refetchOnMountOrArgChange: true
  });

  const [toggleLike] = useToggleLikeMutation();

  const handleLikeClick = async (e) => {
    e.stopPropagation();
    if (!currentUser) {
      if (onUnauthorized) {
        onUnauthorized();
      } else {
        setShowAuthModal(true);
      }
      return;
    }

    const newLikedState = optimisticLiked !== null ? !optimisticLiked : !likeStatus?.isLiked;
    const newCount = (optimisticCount !== null ? optimisticCount : likeStatus?.likeCount || 0) + (newLikedState ? 1 : -1);

    setOptimisticLiked(newLikedState);
    setOptimisticCount(newCount);

    try {
      await toggleLike({ productId });

      // Force refresh of favorites page data if on that page
      if (window.location.pathname.includes('/favorites')) {
        dispatch(handleUrlChange());
      }
    } catch (error) {
      setOptimisticLiked(null);
      setOptimisticCount(null);
      console.error('Error toggling like:', error);
    }
  };

  const sizeClasses = {
    sm: {
      height: 'h-6 sm:h-8',
      width: 'w-6 sm:w-8', // Used only when count is hidden
      icon: 'w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0',
      text: 'text-xs sm:text-sm'
    },
    md: {
      height: 'h-8 sm:h-10',
      width: 'w-8 sm:w-10',
      icon: 'w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0',
      text: 'text-sm sm:text-base'
    },
    lg: {
      height: 'h-10 sm:h-12',
      width: 'w-10 sm:w-12',
      icon: 'w-6 h-6 sm:w-7 sm:h-7 flex-shrink-0',
      text: 'text-base sm:text-lg'
    },
    xl: {
      height: 'h-12 sm:h-14',
      width: 'w-12 sm:w-14',
      icon: 'w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0',
      text: 'text-lg sm:text-xl'
    }
  }[size];

  // Determine button structure classes based on whether count is shown
  const buttonDimensions = showCount
    ? `${sizeClasses.height} w-auto px-3` // Pill shape with padding
    : `${sizeClasses.height} ${sizeClasses.width}`; // Cirlce shape

  if (!currentUser) {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (onUnauthorized) {
              onUnauthorized();
            } else {
              setShowAuthModal(true);
            }
          }}
          className={`${buttonDimensions} flex items-center justify-center rounded-full transition-colors hover:bg-white/90`}
        >
          <HeartOutlineIcon className={`${sizeClasses.icon} text-gray-600`} />
          {showCount && (
            <span className={`${sizeClasses.text} ml-1 text-gray-600`}>
              {likeStatus?.likeCount || 0}
            </span>
          )}
        </button>


        <AuthRequiredModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          message="Please log in to like this product"
          actionType="like products"
        />
      </div>
    );
  }

  const isLiked = optimisticLiked !== null ? optimisticLiked : likeStatus?.isLiked;
  const likeCount = optimisticCount !== null ? optimisticCount : (likeStatus?.likeCount || 0);

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <button
        onClick={handleLikeClick}
        disabled={likeLoading}
        className={`${buttonDimensions} flex items-center justify-center rounded-full transition-colors hover:bg-white/90 disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {isLiked ? (
          <HeartIcon className={`${sizeClasses.icon} text-brand-orange`} />
        ) : (
          <HeartOutlineIcon className={`${sizeClasses.icon} text-gray-600`} />
        )}
        {showCount && (
          <span className={`${sizeClasses.text} ml-1 text-gray-600`}>
            {likeCount}
          </span>
        )}
      </button>

    </div>
  );
};

export default Like;